import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';
import { Actions, setCustomerVehiclesSlideoutVisibility } from '@makemydeal/dr-sales-checkin-store';
import {
    FETCH_DEALS_SUCCESS,
    FETCH_TRADE_INS_SUCCESS,
    CONTINUE_TO_APPLY_DEAL,
    showTradeInModal,
    hideTradeInModal,
    fetchTradeInsForDeal,
    setSelectedDeal,
    buildSourceDealIdentifiers,
    getDeduplicatedTradeIns,
    getCustomerVehicleDeals,
    type GroupedDeal
} from '@makemydeal/dr-activities-customer-vehicles';
import type { ITradeIn } from '@makemydeal/dr-platform-types';
import { VEHICLE_CHANGE_SUCCESS } from '@makemydeal/dr-activities-change-vehicle';
import { SAVE_PARTICIPANTS_SUCCESS } from '@makemydeal/dr-activities-customer-card';
import { getShowRoomUrlWithVersion, syncDealXgVersionInUrl } from '@makemydeal/dr-sales-checkin-ui-utils';
import { updateSCITradeIn } from '@makemydeal/dr-dash-store/actions/tradeInComponentActionCreators';
import { TRADE_IN_UPDATE_SUCCESS, TRADE_IN_REMOVE_SUCCESS } from '@makemydeal/dr-dash-store/actionTypes/tradeActionTypes';
import { SALES_CHECKIN_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { stripTradeInModalUrlParams, refreshCustomerVehicleDeals } from './utils';

export const customerVehiclesMiddleware = (store: ReduxStore<any>) => {
    let pendingTradeInModalUpdate = false;
    // One-shot: the init* values below are captured once and never change, so without this the
    // deep-link handling re-fires on every later refresh in this tab — e.g. after a Browse
    // Inventory vehicle add — re-selecting the stale page-load version.
    let hasHandledInitialTradeInModal = false;

    // Capture transient URL params before navigationMiddleware strips them.
    const initParams = new URLSearchParams(window.location.search);
    const initIsShowTradeInModal = initParams.get('isShowTradeInModal') === 'true';
    const initDealXgId = initParams.get('dealXgId');
    const initDealXgVersion = initParams.get('dealXgVersion');

    return (next: ReduxNext) => (action: AnyFSA) => {
        next(action);

        switch (action.type) {
            case Actions.OPEN_DEAL_FROM_CUSTOMER_VEHICLES: {
                const { dealId, version } = action.payload ?? {};
                if (dealId && version) {
                    const url = getShowRoomUrlWithVersion(dealId, version);
                    window.open(url, '_self');
                    store.dispatch(setCustomerVehiclesSlideoutVisibility(false));
                }
                break;
            }

            case FETCH_DEALS_SUCCESS: {
                const state = store.getState();
                const deals: GroupedDeal[] = getCustomerVehicleDeals(state);

                // No active-deal sync here: the Deal List reads activeDeal directly (see
                // additionalSelectors.ts), which every mutation path keeps current.

                if (!initIsShowTradeInModal || !initDealXgId || !initDealXgVersion || hasHandledInitialTradeInModal) break;
                hasHandledInitialTradeInModal = true;

                store.dispatch(setSelectedDeal(initDealXgId, initDealXgVersion) as unknown as AnyFSA);

                const sourceDeals = buildSourceDealIdentifiers(deals);
                const dealIdentifiers =
                    sourceDeals.length > 0 ? sourceDeals : [{ dealXgId: initDealXgId, version: initDealXgVersion }];

                store.dispatch(fetchTradeInsForDeal(dealIdentifiers) as unknown as AnyFSA);
                break;
            }

            case FETCH_TRADE_INS_SUCCESS: {
                const state = store.getState() ?? {};
                const currentDealTradeIns = state.salesCheckin?.activeDeal?.details?.tradeInVehicles?.tradeIns ?? [];
                const currentDealVins = new Set(
                    currentDealTradeIns
                        .map((trade: { vin?: string }) => trade?.vin)
                        .filter((vin: string | undefined): vin is string => !!vin)
                );
                // Use customerVehicles.deals (post-reducer) rather than the raw payload
                // to avoid opening the modal when trades aren't attached to any deal card.
                const renderableTradeIns: ITradeIn[] = getDeduplicatedTradeIns(getCustomerVehicleDeals(state));
                const hasActionableTrades = renderableTradeIns.some((trade) => {
                    if (!trade.vin) return true;
                    return !currentDealVins.has(trade.vin);
                });

                if (!hasActionableTrades) break;
                store.dispatch(showTradeInModal() as AnyFSA);
                break;
            }

            case Actions.CLOSE_TRADE_IN_MODAL: {
                stripTradeInModalUrlParams();
                store.dispatch(hideTradeInModal() as AnyFSA);
                break;
            }

            case CONTINUE_TO_APPLY_DEAL: {
                stripTradeInModalUrlParams();
                const vinIds: string[] = action.payload ?? [];
                const state = store.getState();

                const dealXgId: string | undefined = state.salesCheckin?.activeDeal?.details?.dealId;
                const dealXgVersion: string | undefined = state.salesCheckin?.activeDeal?.details?.dealStructure?.version;
                const salesPersonUsername: string | undefined = state.bridgeUser?.username;
                // Required: no dealer.mmdId -> BFF marks isDealershipAvailable false -> applicationState
                // INVALID -> navigationMiddleware flashes Deal Setup until the next bootstrap corrects it.
                const dealerMMDId: string | undefined = state.dealer?.dealerMMDId;
                const salesViewMode = (state.dealer?.dealerSettings as any)?.salesViewMode;
                const allDeals: GroupedDeal[] = getCustomerVehicleDeals(state);

                // Check VINs against both activeDeal and customerVehicles to avoid stale-state mismatches.
                const activeDealTradeIns = state.salesCheckin?.activeDeal?.details?.tradeInVehicles?.tradeIns ?? [];
                const currentDealVersion = allDeals
                    .flatMap((deal: GroupedDeal) => deal.deals ?? [])
                    .find((dealVersion) => dealVersion.dealId === dealXgId && dealVersion.version === dealXgVersion);
                const currentDealVersionTradeIns = currentDealVersion?.tradeInVehicles?.tradeIns ?? [];
                const currentDealVins = new Set(
                    [...activeDealTradeIns, ...currentDealVersionTradeIns]
                        .map((trade: { vin?: string }) => trade?.vin)
                        .filter((vin: string | undefined): vin is string => !!vin)
                );
                const actionableVinIds = vinIds.filter((vin) => !currentDealVins.has(vin));

                if (!dealXgId || actionableVinIds.length === 0) break;

                // Combine existing trade-ins with newly selected ones so all are applied in one update.
                const allTradeIns = allDeals.flatMap((deal: GroupedDeal) => deal.tradeIns ?? []);
                const tradeInsByVin = new Map(
                    allTradeIns
                        .filter((trade: ITradeIn) => trade.vin != null)
                        .map((trade: ITradeIn) => [trade.vin as string, trade])
                );
                const tradeInsToApply = actionableVinIds
                    .map((vin) => tradeInsByVin.get(vin))
                    .filter((trade): trade is ITradeIn => !!trade);

                if (tradeInsToApply.length === 0) break;

                const currentTradeIns = Array.from(currentDealVins)
                    .map((vin) => tradeInsByVin.get(vin))
                    .filter((trade): trade is ITradeIn => !!trade);
                const tradeIn = [...currentTradeIns, ...tradeInsToApply];

                pendingTradeInModalUpdate = true;
                store.dispatch(
                    updateSCITradeIn(SALES_CHECKIN_APP_PREFIX, {
                        tradeIn,
                        dealXgId,
                        dealXgVersion,
                        salesPersonUsername,
                        dealer: { mmdId: dealerMMDId },
                        salesViewMode
                    }) as unknown as AnyFSA
                );
                store.dispatch(hideTradeInModal() as AnyFSA);
                break;
            }
            // Deal moved to a new version — refresh the Deal List's data. The version needs nothing
            // here: these responses carry the updated deal and activeDeal's own reducer adopts it.
            case Actions.FETCH_DEAL_SUCCESS:
            case Actions.UPDATE_DEAL_SUCCESS:
            case VEHICLE_CHANGE_SUCCESS:
            case SAVE_PARTICIPANTS_SUCCESS: {
                refreshCustomerVehicleDeals(store);
                break;
            }

            case TRADE_IN_REMOVE_SUCCESS: {
                refreshCustomerVehicleDeals(store);
                break;
            }

            case TRADE_IN_UPDATE_SUCCESS: {
                refreshCustomerVehicleDeals(store);

                const hasTradeInApplied = pendingTradeInModalUpdate;
                if (hasTradeInApplied) pendingTradeInModalUpdate = false;

                // The reload re-bootstraps the deal, so no refetch needed here. But the URL was
                // stripped of dealXgVersion earlier (see stripTradeInModalUrlParams) and is the sole
                // source of truth persistSearchParams uses to pin a version on load — restore it to
                // the version this save created, or the reload lands on latest and the trade looks
                // dropped. With no savedVersion, the URL already has no dealXgVersion, so the reload
                // correctly falls through to latest.
                if (hasTradeInApplied && initIsShowTradeInModal) {
                    const savedVersion = action.payload?.data?.dealXgVersion;
                    if (savedVersion) {
                        syncDealXgVersionInUrl(savedVersion);
                    }
                    window.location.reload();
                    break;
                }

                break;
            }
        }
    };
};
